import { Children, isValidElement, type ReactElement, type ReactNode } from "react";

import { Button } from "@/web/components/primitives/button";
import { Tooltip, type TooltipColor, TooltipTrigger } from "@/web/components/primitives/tooltip";
import { ErrorHandler } from "@/web/lib/errors";
import { useReducerMutation } from "@/web/lib/tanstack";
import { reducers } from "@tycho/web-bindings";

import { useAcknowledgments } from "./AcknowledgmentsProvider";
import { type MessageKey } from "./messageKeys";
import { AckTooltipShell } from "./shells";

/**
 * Sequence of acknowledgment tooltips attached to a single anchor. Children
 * are a flat list of:
 *   - any number of `<AcknowledgmentTooltipStep>` declarations (metadata
 *     only — these don't render anything themselves), and
 *   - exactly one anchor element (the thing the tooltip points at).
 *
 * The first step whose gate passes is opened; if none pass, the anchor still
 * renders, just without an open bubble. Order matters: steps are evaluated
 * top-to-bottom, first-match wins. To express "B comes after A", put A
 * first; A's gate will keep firing until A is acked, at which point B
 * becomes eligible.
 *
 * A step's gate is `when && !shouldDefer(key) && !isAcked(messageKey)` —
 * identical to the modal rule. The orientation tour still owns the
 * foreground first; ack-tooltips defer.
 *
 * The anchor child must `forwardRef` (Radix `asChild` requires it).
 */
export function AcknowledgmentTooltip({
  side = "top",
  color,
  acknowledgeLabel = "Got it",
  children,
}: {
  side?: "top" | "bottom" | "left" | "right";
  color?: TooltipColor;
  acknowledgeLabel?: string;
  children: ReactNode;
}) {
  const { isAcked, shouldDefer, underwritableEntityId } = useAcknowledgments();

  const { mutateAsync: acknowledge, isPending } = useReducerMutation(reducers.acknowledgeMessage, {
    onError: ErrorHandler.default,
  });

  // Split children into step declarations and the lone anchor element.
  const steps: StepProps[] = [];
  let anchor: ReactElement | null = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === AcknowledgmentTooltipStep) {
      steps.push(child.props as StepProps);
    } else {
      if (anchor) {
        throw new Error(
          "<AcknowledgmentTooltip> accepts exactly one anchor child; wrap multiple elements in a single parent.",
        );
      }
      anchor = child;
    }
  });

  if (!anchor) {
    throw new Error(
      "<AcknowledgmentTooltip> requires one anchor child alongside its <AcknowledgmentTooltipStep> entries.",
    );
  }

  const activeStep = steps.find((step) => {
    const when = step.when ?? true;
    return when && !shouldDefer(step.messageKey) && !isAcked(step.messageKey);
  });

  const onAcknowledge = () => {
    if (!activeStep) return;
    void acknowledge({ underwritableEntityId, messageKey: activeStep.messageKey });
  };

  // Always render the Tooltip wrapper so the anchor stays mounted; control
  // visibility via `open` so closing is animated and the anchor doesn't
  // jump when no step is active.
  return (
    <Tooltip open={!!activeStep}>
      <TooltipTrigger asChild>{anchor}</TooltipTrigger>
      <AckTooltipShell
        side={side}
        color={color}
        title={activeStep?.title}
        body={activeStep?.body}
        footer={
          <Button
            variant="link"
            size="sm"
            onClick={onAcknowledge}
            disabled={isPending || !activeStep}
          >
            {acknowledgeLabel}
          </Button>
        }
      />
    </Tooltip>
  );
}

type StepProps = {
  messageKey: MessageKey;
  when?: boolean;
  title: ReactNode;
  body: ReactNode;
};

/**
 * Declarative metadata for one entry in an `<AcknowledgmentTooltip>`
 * sequence. Renders nothing itself — the parent introspects children to
 * decide which step (if any) to open.
 */
export function AcknowledgmentTooltipStep(_props: StepProps) {
  return null;
}
